const WISHLIST_KEY = 'esprit_wishlist';
const MAX_IDS = 100;
const WISHLIST_CHANGE_EVENT = 'wishlist:change';

class WishlistStore {
  #items = null;

  #read() {
    if (Array.isArray(this.#items)) return this.#items;

    let parsed = [];

    try {
      const raw = window.localStorage.getItem(WISHLIST_KEY);
      if (raw) {
        const value = JSON.parse(raw);
        if (Array.isArray(value)) {
          parsed = value;
        }
      }
    } catch (_) {
      parsed = [];
    }

    const safe = [];
    const seen = new Set();
    for (const item of parsed) {
      const id = typeof item === 'object' && item !== null
        ? String(item.id ?? '').trim()
        : String(item ?? '').trim();
      if (!/^\d+$/.test(id) || seen.has(id)) continue;
      seen.add(id);
      safe.push(this.#normalize({ ...(typeof item === 'object' && item !== null ? item : {}), id }));
      if (safe.length >= MAX_IDS) break;
    }

    this.#items = safe;
    return this.#items;
  }

  #normalize(item) {
    const id = String(item?.id ?? '').trim();
    return {
      id,
      handle: String(item?.handle ?? '').trim(),
      url: String(item?.url ?? '').trim(),
      title: String(item?.title ?? '').trim(),
      image: String(item?.image ?? '').trim(),
      imageAlt: String(item?.imageAlt ?? item?.title ?? '').trim(),
      price: String(item?.price ?? '').trim(),
    };
  }

  #write(items) {
    this.#items = items.slice(0, MAX_IDS).map((item) => this.#normalize(item));
    try {
      window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(this.#items));
    } catch (_) {
      // no-op
    }
  }

  #emit(productId, action) {
    const ids = this.list();
    window.dispatchEvent(
      new CustomEvent(WISHLIST_CHANGE_EVENT, {
        detail: { productId, action, ids, count: ids.length },
      })
    );
  }

  add(product) {
    const item = this.#normalize(typeof product === 'object' && product !== null ? product : { id: product });
    const id = item.id;
    if (!/^\d+$/.test(id)) return false;
    const items = this.#read();
    if (items.some((entry) => entry.id === id) || items.length >= MAX_IDS) return false;
    items.push(item);
    this.#write(items);
    this.#emit(id, 'add');
    return true;
  }

  remove(productId) {
    const id = String(productId ?? '').trim();
    const items = this.#read();
    const next = items.filter((item) => item.id !== id);
    if (next.length === items.length) return false;
    this.#write(next);
    this.#emit(id, 'remove');
    return true;
  }

  toggle(product) {
    const id = typeof product === 'object' && product !== null ? product.id : product;
    return this.contains(id) ? this.remove(id) : this.add(product);
  }

  contains(productId) {
    const id = String(productId ?? '').trim();
    return this.#read().some((item) => item.id === id);
  }

  count() {
    return this.#read().length;
  }

  list() {
    return this.#read().map((item) => item.id);
  }

  items() {
    return this.#read().map((item) => ({ ...item }));
  }

  updateMetadata(product) {
    const item = this.#normalize(product);
    if (!/^\d+$/.test(item.id)) return false;
    const items = this.#read();
    const index = items.findIndex((entry) => entry.id === item.id);
    if (index === -1) return false;
    const next = { ...items[index], ...Object.fromEntries(Object.entries(item).filter(([, value]) => value)) };
    items[index] = next;
    this.#write(items);
    return true;
  }
}

const wishlistStore = new WishlistStore();

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.productId = String(this.dataset.productId ?? '').trim();
    if (!(this.button instanceof HTMLButtonElement) || !/^\d+$/.test(this.productId)) return;

    this.addLabel = this.dataset.addLabel || '';
    this.removeLabel = this.dataset.removeLabel || '';
    this.defaultLabel = this.dataset.defaultLabel || this.addLabel;
    this.product = this.getProductMetadata();
    this.handleToggle = this.handleToggle.bind(this);
    this.handleChange = this.handleChange.bind(this);

    this.button.addEventListener('click', this.handleToggle);
    window.addEventListener(WISHLIST_CHANGE_EVENT, this.handleChange);
    if (wishlistStore.contains(this.productId)) wishlistStore.updateMetadata(this.product);
    this.sync();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.handleToggle);
    window.removeEventListener(WISHLIST_CHANGE_EVENT, this.handleChange);
  }

  handleToggle(event) {
    event.preventDefault();
    event.stopPropagation();
    wishlistStore.toggle(this.product);
  }

  getProductMetadata() {
    return {
      id: this.productId,
      handle: this.dataset.productHandle || '',
      url: this.dataset.productUrl || '',
      title: this.dataset.productTitle || '',
      image: this.dataset.productImage || '',
      imageAlt: this.dataset.productImageAlt || this.dataset.productTitle || '',
      price: this.dataset.productPrice || '',
    };
  }

  handleChange() {
    this.sync();
  }

  sync() {
    const active = wishlistStore.contains(this.productId);
    this.button?.setAttribute('aria-pressed', active ? 'true' : 'false');
    this.button?.setAttribute('aria-label', active ? this.removeLabel : this.addLabel);
    const label = this.querySelector('[data-wishlist-label]');
    if (label) label.textContent = active ? this.removeLabel : this.defaultLabel;
    this.classList.toggle('is-active', active);
  }
}

class WishlistCount extends HTMLElement {
  connectedCallback() {
    this.badge = this.querySelector('[data-wishlist-count]') || this;
    this.handleChange = this.handleChange.bind(this);
    window.addEventListener(WISHLIST_CHANGE_EVENT, this.handleChange);
    this.sync();
  }

  disconnectedCallback() {
    window.removeEventListener(WISHLIST_CHANGE_EVENT, this.handleChange);
  }

  handleChange() {
    this.sync();
  }

  sync() {
    const count = wishlistStore.count();
    this.badge.textContent = String(count);
    this.hidden = count === 0;
  }
}

class WishlistPage extends HTMLElement {
  connectedCallback() {
    this.productSectionId = this.dataset.productSectionId || 'gco-wishlist-drawer-card';
    this.maxProducts = Number(this.dataset.maxProducts || 100);
    this.items = this.querySelector('[data-wishlist-items]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.missing = this.querySelector('[data-wishlist-missing]');
    this.skeleton = this.querySelector('[data-wishlist-skeleton]');
    this.handleChange = this.handleChange.bind(this);
    this.isHydrating = false;
    this.pendingHydrate = false;
    this.abortController = null;
    this.hydrated = false;
    this.cardHtmlCache = new Map();
    this.hydrateMode = (this.dataset.hydrateMode || '').trim();
    this.addLabel = this.dataset.addLabel || 'Add to wishlist';
    this.removeLabel = this.dataset.removeLabel || 'Remove from wishlist';
    this.defaultLabel = this.dataset.defaultLabel || 'Wishlist';
    this.hydrateOnConnect =
      this.hydrateMode === 'immediate'
      || this.dataset.hydrateOnConnect === 'true';

    window.addEventListener(WISHLIST_CHANGE_EVENT, this.handleChange);

    if (!this.hydrateOnConnect) {
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.observer?.disconnect();
          this.hydrate();
        }
      }, { rootMargin: '250px' });

      this.observer.observe(this);
    } else {
      this.observer = null;
      this.hydrate();
    }
  }

  disconnectedCallback() {
    window.removeEventListener(WISHLIST_CHANGE_EVENT, this.handleChange);
    this.observer?.disconnect();
    this.abortController?.abort();
  }

  handleChange(event) {
    if (event?.detail?.action === 'hydrate') return;

    if (this.isHydrating) {
      this.pendingHydrate = true;
      return;
    }

    this.hydrated = false;
    this.hydrate();
  }

  async hydrate() {
    if (this.hydrated && !this.pendingHydrate) return;

    if (this.isHydrating) {
      this.pendingHydrate = true;
      return;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();
    this.isHydrating = true;
    this.hydrated = true;

    const items = wishlistStore.items().slice(0, this.maxProducts);
    if (items.length === 0) {
      this.renderEmpty();
      this.finishHydrate();
      return;
    }

    this.setAttribute('aria-busy', 'true');
    this.skeleton.hidden = false;
    this.empty.hidden = true;
    if (this.missing) this.missing.hidden = true;

    const renderableItems = items.filter((item) => item.handle);
    if (renderableItems.length === 0) {
      this.renderMissing();
      this.finishHydrate();
      return;
    }

    const ordered = [];
    const fetchFailures = [];

    try {
      const htmlResults = await Promise.all(
        renderableItems.map(async (item) => {
          try {
            const html = await this.fetchCardHtmlByHandle(item.handle);
            return { item, html };
          } catch (_) {
            fetchFailures.push(item.handle);
            return { item, html: '' };
          }
        })
      );

      const liveIds = new Set(wishlistStore.list().slice(0, this.maxProducts));
      for (const result of htmlResults) {
        if (!liveIds.has(result.item.id)) continue;
        if (result.html) ordered.push(result.html);
      }
    } catch (_) {
      // no-op
    }

    if (ordered.length === 0) {
      if (fetchFailures.length > 0) {
        this.skeleton.hidden = true;
        this.items.hidden = true;
        this.empty.hidden = true;
        if (this.missing) this.missing.hidden = false;
      } else {
        this.renderMissing();
      }
      this.finishHydrate();
      return;
    }

    this.items.innerHTML = ordered.join('');
    this.items.hidden = false;
    this.empty.hidden = true;
    if (this.missing) this.missing.hidden = true;
    this.skeleton.hidden = true;
    this.removeAttribute('aria-busy');

    window.dispatchEvent(new CustomEvent(WISHLIST_CHANGE_EVENT, { detail: { action: 'hydrate', ids: wishlistStore.list(), count: wishlistStore.count() } }));
    this.finishHydrate();
  }

  async fetchCardHtmlByHandle(handle) {
    const safeHandle = String(handle ?? '').trim();
    if (!safeHandle) return '';

    if (this.cardHtmlCache.has(safeHandle)) {
      return this.cardHtmlCache.get(safeHandle) || '';
    }

    const url = new URL(`/products/${encodeURIComponent(safeHandle)}`, window.location.origin);
    url.searchParams.set('section_id', this.productSectionId);

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      signal: this.abortController?.signal,
    });

    if (!response.ok) {
      this.cardHtmlCache.set(safeHandle, '');
      return '';
    }

    const text = await response.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const cards = Array.from(doc.querySelectorAll('[data-wishlist-product-handle]'));
    const card = cards.find((node) => node.getAttribute('data-wishlist-product-handle') === safeHandle);
    const html = card?.outerHTML || '';
    this.cardHtmlCache.set(safeHandle, html);
    return html;
  }

  renderEmpty() {
    this.items.innerHTML = '';
    this.items.hidden = true;
    this.skeleton.hidden = true;
    this.empty.hidden = false;
    if (this.missing) this.missing.hidden = true;
    this.removeAttribute('aria-busy');
  }

  renderMissing() {
    this.items.innerHTML = '';
    this.items.hidden = true;
    this.skeleton.hidden = true;
    this.empty.hidden = true;
    if (this.missing) this.missing.hidden = false;
    this.removeAttribute('aria-busy');
  }

  finishHydrate() {
    this.isHydrating = false;
    this.removeAttribute('aria-busy');
    if (this.pendingHydrate) {
      this.pendingHydrate = false;
      this.hydrate();
    }
  }
}

customElements.define('wishlist-button', WishlistButton);
customElements.define('wishlist-count', WishlistCount);
customElements.define('wishlist-page', WishlistPage);

export { wishlistStore, WISHLIST_CHANGE_EVENT };
